import * as React from "react";
import type { DateRange } from "react-day-picker";
import { Calendar as CalendarIcon } from "lucide-react";

import { Button } from "@src/ui/views/button";
import { Calendar } from "@src/ui/views/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@src/ui/views/popover";
import { isoFormatDate, parseISODate, prettyFormatDate } from "@src/utils/date-utils";

export type DateRangeValue = {
	/** YYYY-MM-DD, or undefined when unset. */
	startDate?: string;
	endDate?: string;
};

export type DateRangePickerProps = {
	value: DateRangeValue;
	onChange: (value: DateRangeValue) => void;
	className?: string;
};

const label = (value: DateRangeValue): string => {
	if (!value.startDate && !value.endDate) return "Pick a date range";
	if (value.startDate && !value.endDate) {
		return `${prettyFormatDate(parseISODate(value.startDate))} → …`;
	}
	if (!value.startDate && value.endDate) {
		return `… → ${prettyFormatDate(parseISODate(value.endDate))}`;
	}
	return `${prettyFormatDate(parseISODate(value.startDate!))} → ${prettyFormatDate(
		parseISODate(value.endDate!),
	)}`;
};

/** Two-month range calendar returning ISO date strings. */
export function DateRangePicker({ value, onChange, className }: DateRangePickerProps) {
	const selected: DateRange | undefined = value.startDate
		? {
				from: parseISODate(value.startDate),
				to: value.endDate ? parseISODate(value.endDate) : undefined,
			}
		: undefined;

	/**
	 * Strict alternating selection: click 1 sets the start, click 2 sets the
	 * end, click 3 starts a fresh range, and so on. react-day-picker's own
	 * range handler instead grows/shrinks the existing range, which makes the
	 * start date impossible to move forward without clearing first.
	 */
	const handleDayClick = (day: Date) => {
		const clicked = isoFormatDate(day);
		const rangeIsComplete = Boolean(value.startDate && value.endDate);

		if (!value.startDate || rangeIsComplete) {
			onChange({ startDate: clicked, endDate: undefined });
			return;
		}

		// A start exists and this click closes the range. If the user picked an
		// earlier day, treat it as the new start so the range is never inverted.
		if (clicked < value.startDate) {
			onChange({ startDate: clicked, endDate: value.startDate });
		} else {
			onChange({ startDate: value.startDate, endDate: clicked });
		}
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={className ?? "w-[290px] justify-start text-left font-normal"}
				>
					<CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
					<span className="truncate">{label(value)}</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="end">
				{/* `selected` drives the highlight; `onDayClick` drives selection. */}
				<Calendar
					mode="range"
					numberOfMonths={2}
					defaultMonth={selected?.from}
					selected={selected}
					onDayClick={handleDayClick}
					disabled={{ after: new Date() }}
					initialFocus
				/>
			</PopoverContent>
		</Popover>
	);
}
